import React from "react";
import { HeartHandshake, UtensilsCrossed, Sparkles, Stethoscope, ArrowUpRight } from "lucide-react";

const SERVICES = [
  {
    id: "companionship",
    icon: HeartHandshake,
    title: "In-Home Care & Companionship",
    body:
      "Wellness check-ins, gentle mobility support, personal hygiene assistance, non-medical medication reminders, and warm daily companionship.",
    bullets: [
      "Compassion-driven wellness visits",
      "Light exercise & activity accompaniment",
      "Bathing, dressing & hygiene support",
    ],
  },
  {
    id: "meals",
    icon: UtensilsCrossed,
    title: "Grocery & Meal Preparation",
    body:
      "Bespoke grocery shopping aligned with dietary needs and Jamaican culinary preferences, plus nutrient-dense weekly meal prep.",
    bullets: [
      "Senior-conscious meal planning",
      "Cultural & dietary tailoring",
      "Trusted local delivery partners",
    ],
  },
  {
    id: "home",
    icon: Sparkles,
    title: "Home Cleaning & Safety",
    body:
      "Routine housekeeping, monthly deep cleaning, and comprehensive home safety assessments designed to eliminate fall risks.",
    bullets: [
      "Light housekeeping & laundry",
      "Quarterly deep cleaning",
      "Fall-risk safety audits",
    ],
  },
  {
    id: "medical",
    icon: Stethoscope,
    title: "Medical Coordination",
    body:
      "Proactive appointment scheduling, accompanied transportation, pharmacy coordination, and real-time updates to family overseas (with consent).",
    bullets: [
      "Calendar & reminder management",
      "Accompanied transport",
      "Family transparency updates",
    ],
  },
];

const ServiceCard = ({ service: s, featured }) => {
  const Icon = s.icon;
  return (
    <article
      data-testid={`service-card-${s.id}`}
      className={`group relative rounded-2xl border transition-all duration-500 ${
        featured
          ? "md:col-span-2 bg-[var(--gg-cream)] border-[var(--gg-secondary)]/40 p-10 md:p-14"
          : "bg-white border-gray-100 p-8 md:p-10 hover:-translate-y-1 hover:shadow-md"
      }`}
    >
      <div
        className={`flex ${
          featured ? "md:flex-row flex-col md:items-end md:gap-16" : "flex-col"
        } gap-8`}
      >
        <div className={featured ? "md:flex-1" : ""}>
          <span className="inline-flex w-12 h-12 items-center justify-center rounded-full bg-[var(--gg-primary)]/10 text-[var(--gg-primary)] mb-6">
            <Icon size={22} strokeWidth={1.5} />
          </span>
          <h3 className="font-serif text-2xl md:text-3xl text-[var(--gg-deep)] tracking-tight">
            {s.title}
          </h3>
          <p className="mt-4 text-[var(--gg-text)] leading-relaxed">{s.body}</p>
        </div>
        <ul
          className={`space-y-2.5 ${
            featured ? "md:flex-1 md:border-l md:pl-12 border-[var(--gg-secondary)]/50" : "mt-2"
          }`}
        >
          {s.bullets.map((b) => (
            <li key={b} className="flex items-start gap-3 text-sm text-[var(--gg-text)]">
              <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[var(--gg-primary)]" />
              {b}
            </li>
          ))}
        </ul>
      </div>

      <a
        href="#contact"
        data-testid={`service-cta-${s.id}`}
        className="absolute top-8 right-8 w-10 h-10 rounded-full border border-[var(--gg-primary)]/30 flex items-center justify-center text-[var(--gg-primary)] group-hover:bg-[var(--gg-primary)] group-hover:text-white transition-colors"
        aria-label={`Inquire about ${s.title}`}
      >
        <ArrowUpRight size={16} />
      </a>
    </article>
  );
};

export default function ServicesSection() {
  return (
    <section
      id="care"
      data-testid="services-section"
      className="relative honeycomb py-28 md:py-36 bg-[var(--gg-background)]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-20">
          <span className="eyebrow">In-Home Care & Support</span>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[var(--gg-deep)] mt-5 leading-[1.05] text-balance">
            A quieter kind of strength,
            <br />
            <em className="not-italic text-[var(--gg-primary)] font-normal">
              brought into the home.
            </em>
          </h2>
          <p className="mt-7 text-lg text-[var(--gg-text)] leading-relaxed max-w-2xl">
            Designed for seniors ageing in place. We bring trained,
            background-checked caregivers into your home so that everyday life
            stays familiar, safe, and dignified.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {SERVICES.map((s, i) => (
            <ServiceCard key={s.id} service={s} featured={i === 0} />
          ))}
        </div>
      </div>
    </section>
  );
}
